import { useRef, useState } from "react";
import { fetchComplianceDocuments, linkComplianceDocument, unlinkComplianceDocument, downloadComplianceDocument } from "@/lib/api";

// "Evidence" control shown next to a profile field, filing, books registration,
// ORUS package or tax year. It lists the documents already LINKED to that record
// (the file bytes are stored once, in the Document Center — this only references
// them), lets an authorised user open one directly, and link/unlink another.
export default function EvidenceLinks({ token, businessId, kind, refId, label, canManage = false, canDownload = false }) {
  const [open, setOpen] = useState(false);
  const [linked, setLinked] = useState(null);
  const [allDocs, setAllDocs] = useState([]);
  const [pick, setPick] = useState("");
  const [error, setError] = useState("");
  const toggleRef = useRef(null);

  // Escape closes the popover from anywhere inside it and hands focus back to the
  // control that opened it — keyboard users are never left stranded in a closed panel.
  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      setOpen(false);
      toggleRef.current?.focus();
    }
  };

  const load = async () => {
    setError("");
    try {
      const [l, all] = await Promise.all([
        fetchComplianceDocuments(token, businessId, kind, refId),
        canManage ? fetchComplianceDocuments(token, businessId) : Promise.resolve([]),
      ]);
      setLinked(l);
      setAllDocs(all);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load evidence.");
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const link = async () => {
    if (!pick) return;
    setError("");
    try {
      await linkComplianceDocument(token, businessId, pick, kind, refId, label);
      setPick("");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not link this document.");
    }
  };

  const unlink = async (docId) => {
    setError("");
    try {
      await unlinkComplianceDocument(token, businessId, docId, kind, refId);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not remove this link.");
    }
  };

  const linkedIds = new Set((linked || []).map((d) => d.id));
  const candidates = allDocs.filter((d) => !linkedIds.has(d.id));
  return (
    <span className="inline-block" data-testid={`evidence-${kind}-${refId}`} onKeyDown={onKeyDown}>
      <button type="button" ref={toggleRef} onClick={toggle} aria-expanded={open} className="font-semibold text-[#0F172A] hover:underline">Evidence</button>
      {open && (
        <div role="group" aria-label={`Evidence for ${label || refId}`} className="mt-2 w-72 max-w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-left text-xs font-normal">
          {error && <p className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700">{error}</p>}
          {linked === null && !error && <p className="text-[#475569]">Loading…</p>}
          {linked && linked.length === 0 && <p className="text-[#475569]">No evidence linked yet.</p>}
          {linked && linked.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 py-1">
              <span className="truncate font-medium text-[#0F172A]" title={d.filename}>{d.filename}</span>
              <span className="flex shrink-0 gap-2">
                {canDownload && <button type="button" onClick={() => downloadComplianceDocument(token, businessId, d.id, d.filename)} className="font-semibold text-[#10B981] hover:underline">Open</button>}
                {canManage && <button type="button" onClick={() => unlink(d.id)} className="font-semibold text-red-600 hover:underline">Unlink</button>}
              </span>
            </div>
          ))}
          {canManage && (
            <div className="mt-2 flex gap-2 border-t border-[#E2E8F0] pt-2">
              <select aria-label={`Document to link as evidence for ${label || refId}`} value={pick} onChange={(e) => setPick(e.target.value)} className="min-w-0 flex-1 rounded border border-[#E2E8F0] bg-white px-1 py-1">
                <option value="">Link an uploaded document…</option>
                {candidates.map((d) => <option key={d.id} value={d.id}>{d.filename}</option>)}
              </select>
              <button type="button" onClick={link} disabled={!pick} className="rounded bg-[#10B981] px-2 py-1 font-semibold text-white disabled:opacity-40">Link</button>
            </div>
          )}
          {canManage && candidates.length === 0 && linked && <p className="mt-1 text-[#94A3B8]">Upload documents in the Document Center first.</p>}
        </div>
      )}
    </span>
  );
}
